<!--
Copyright 2021 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <modal id="analytics-introduction" :state="state" hideable backdrop
    @hide="$emit('hide')">
    <template #title>{{ $t('title') }}</template>
    <template #body>
      <div class="modal-introduction">
        <p>{{ $t('analytics.alwaysImprove') }}</p>
        <i18n-t tag="p" keypath="analytics.needFeedback.full">
          <template #your>
            <strong>{{ $t('analytics.needFeedback.your') }}</strong>
          </template>
        </i18n-t>
        <i18n-t tag="p" keypath="introduction[0].full">
          <template #usageReporting>
            <router-link v-slot="{ href, navigate }" to="/system/analytics"
              custom>
              <a :href="href" @click="hideAndNavigate(navigate, $event)">{{ $t('introduction[0].usageReporting') }}</a>
            </router-link>
          </template>
        </i18n-t>
        <p>{{ $t('introduction[1]') }}</p>
      </div>
      <div class="modal-actions">
        <router-link v-slot="{ href, navigate }" to="/system/analytics" custom>
          <a class="btn btn-primary" :href="href"
            @click="hideAndNavigate(navigate, $event)">
            {{ $t('action.improveCentral') }}
          </a>
        </router-link>
        <button type="button" class="btn btn-link" @click="$emit('hide')">
          {{ $t('action.close') }}
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from '../modal.vue';

export default {
  name: 'AnalyticsIntroduction',
  components: { Modal },
  props: {
    state: Boolean
  },
  emits: ['hide'],
  methods: {
    hideAndNavigate(navigate, event) {
      this.$emit('hide');
      navigate(event);
    }
  }
};
</script>

<i18n lang="json5">
{
  "en": {
    // This is the title at the top of a pop-up.
    "title": "Help Improve Central!",
    "introduction": [
      {
        "full": "In the {usageReporting} tab in System Settings, you can choose to share anonymized usage data or contact information with the Central team.",
        "usageReporting": "Usage Reporting"
      },
      "There, you can also choose to not see this message again."
    ],
    "action": {
      "improveCentral": "Improve Central"
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "title": "Pomozte zlepšit Central!",
    "introduction": [
      {
        "full": "Na kartě {usageReporting} v Nastavení systému můžete zvolit sdílení anonymizovaných údajů o používání nebo kontaktních informací s týmem Central.",
        "usageReporting": "Hlášení o použití"
      },
      "Tam můžete také zvolit, že se tato zpráva již nebude zobrazovat."
    ],
    "action": {
      "improveCentral": "Zlepšit Central"
    }
  },
  "de": {
    "title": "Hilf ODK Central zu verbessern!",
    "introduction": [
      {
        "full": "Unter {usageReporting} in den Systemeinstellungen kannst du wählen, ob duanonymisierte Nutzungsdaten oder Kontaktinformationen mit dem Central-Team teilen möchtest.",
        "usageReporting": "Nutzungsberichterstattung"
      },
      "Dort können Sie auch festlegen, dass diese Nachricht nicht mehr angezeigt wird."
    ],
    "action": {
      "improveCentral": "Verbessere Central"
    }
  },
  "es": {
    "title": "Ayuda a mejorar Central",
    "introduction": [
      {
        "full": "En la pestaña {usageReporting} en Configuración del sistema, puede optar por compartir datos de uso anónimos o información de contacto con el equipo Central.",
        "usageReporting": "Informes de uso"
      },
      "Allí, también puede optar por no volver a ver este mensaje."
    ],
    "action": {
      "improveCentral": "Mejorar Central"
    }
  },
  "fr": {
    "title": "Aidez à améliorer Central !",
    "introduction": [
      {
        "full": "Dans l'onglet {usageReporting} des Paramètres Système, vous pouvez choisir de partager des données d'usage anonymisées ou vos coordonnées avec l'équipe de Central.",
        "usageReporting": "Rapports d'utilisation"
      },
      "Là, vous pouvez également choisir de ne plus voir à nouveau ce message."
    ],
    "action": {
      "improveCentral": "Améliorer Central"
    }
  },
  "it": {
    "title": "Aiuta a migliorare Central",
    "introduction": [
      {
        "full": "Nella scheda {usageReporting} in Impostazioni di sistema, puoi scegliere di condividere dati di utilizzo anonimi o informazioni di contatto con il team Central.",
        "usageReporting": "Report di utilizzo"
      },
      "Lì, puoi anche scegliere di non vedere più questo messaggio."
    ],
    "action": {
      "improveCentral": "Migliora Central"
    }
  },
  "ja": {
    "title": "Centralの改善に貢献",
    "introduction": [
      {
        "full": "システム管理の{usageReporting}タブで、あなたは匿名の利用データを共有するか、あなたのコンタクトをCentralの開発チームと共有するかを選択できます。",
        "usageReporting": "利用状況報告"
      },
      "そこで、あなたはこのメッセージを再び表示しないように設定できます。"
    ],
    "action": {
      "improveCentral": "Centralを改善する。"
    }
  },
  "sw": {
    "title": "Saidia Kuboresha Central!",
    "introduction": [
      {
        "full": "Katika kichupo cha {usageReporting} katika Mipangilio ya Mfumo, unaweza kuchagua kushiriki data ya matumizi isiyojulikana au maelezo ya mawasiliano na timu ya Kati.",
        "usageReporting": "Ripoti ya Matumizi"
      },
      "Huko, unaweza pia kuchagua kutouona ujumbe huu tena"
    ],
    "action": {
      "improveCentral": "Boresha Central"
    }
  }
}
</i18n>
